import type { PdlExistingLoansDto } from '../api/admin';

const FREQUENCY_LABEL: Record<string, string> = {
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Bi-weekly',
  SEMI_MONTHLY: 'Semi-monthly',
  MONTHLY: 'Monthly',
  IRREGULAR: 'Irregular',
};

export type PdlObligationRow = {
  serial: number;
  lender: string;
  frequency: string;
  paymentCount: number;
  monthlyAmount: number;
  /** Gross before revolving net-flow; only set when it differs from monthlyAmount. */
  grossMonthlyAmount: number | null;
};

export type PdlObligationTable = {
  rows: PdlObligationRow[];
  totalMonthly: number;
};

export function pdlFrequencyLabel(frequency: string | null | undefined): string {
  const key = String(frequency ?? '').toUpperCase();
  return FREQUENCY_LABEL[key] ?? 'Irregular';
}

/**
 * Itemised ELOD obligations → table rows (largest first) + monthly total.
 * Null when there is nothing to itemise, so the panel degrades to the summary only.
 */
export function buildPdlObligationTable(
  pdlExistingLoans: PdlExistingLoansDto | null | undefined,
): PdlObligationTable | null {
  if (
    !pdlExistingLoans ||
    !Array.isArray(pdlExistingLoans.obligations) ||
    pdlExistingLoans.obligations.length === 0
  ) {
    return null;
  }

  const sorted = [...pdlExistingLoans.obligations].sort(
    (a, b) => (b.monthlyAmountCents ?? 0) - (a.monthlyAmountCents ?? 0),
  );
  const rows = sorted.map((o, i): PdlObligationRow => {
    const monthlyAmount = Math.max(0, (o.monthlyAmountCents ?? 0) / 100);
    const gross = Math.max(0, (o.grossMonthlyAmountCents ?? 0) / 100);
    return {
      serial: i + 1,
      lender: o.lender?.trim() || 'Unknown lender',
      frequency: pdlFrequencyLabel(o.frequency),
      paymentCount: o.paymentCount ?? 0,
      monthlyAmount,
      grossMonthlyAmount: Math.round(gross) !== Math.round(monthlyAmount) ? gross : null,
    };
  });

  return {
    rows,
    totalMonthly: rows.reduce((sum, r) => sum + r.monthlyAmount, 0),
  };
}
